
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import NavHeader from "@/components/NavHeader.vue";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import api from "@/services/api.service";
import EventOutcomeSelect from "@/components/form/EventOutcomeSelect.vue";
import TextHighlight from "vue-text-highlight";
import { EventCategory } from "@/store/event-category-setup/types";
import { UserPermissions, FeaturesList, User } from "@/store/types";
import { get } from "lodash";
import { CaseType } from "@/store/case-management/types";
import { SetupState } from "@/store/setup/types";
import CaseTypeSelect from "@/components/form/CaseTypeSelect.vue";
import { AreaNode } from "@/types/groups/AreaNode";

const GenericTableStore = namespace("GenericTable");
const Eventqueue = namespace("eventqueue");
const EventCategoryStore = namespace("eventCategorySetup");
const CaseManagement = namespace("caseManagement");
const Setup = namespace("setup");

@Component({
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"text-highlight": TextHighlight,
	},
})
export default class EventCategorySetup extends Vue {
	@Ref("genericTable") genericTableRef: GenericTable;
	@Getter getPermissions: UserPermissions;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Mutation private setIsLoading: (loading: boolean) => void;

	@State(state => state.User) user: User;

	@GenericTableStore.Getter getModalRow: any;
	@GenericTableStore.Mutation setModalRow: any;

	@EventCategoryStore.Action fetchEventCategoriesList: () => void;
	@EventCategoryStore.Action addEventCategory: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.Action updateEventCategory: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.Action deleteEventCategory: (eventOutcome: EventCategory) => void;

	@EventCategoryStore.Mutation hideOutcome: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.Mutation showOutcome: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.Mutation hideOutcomeSubTree: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.Mutation showOutcomeSubTree: (eventOutcome: EventCategory) => void;
	@EventCategoryStore.State eventCategoriesList: EventCategory[];

	@CaseManagement.State caseTypesForUser: CaseType[];
	@CaseManagement.Action fetchCaseTypesForUser: () => void;

	@Setup.Action("groupIdSelectedChildren") getGroupIdsToChildren: (groupId: number) => Promise<void>;
	@Setup.State((state: SetupState) => state.currentAreas) private groupIdsToChildren: Set<number>;

	
	private toggleMobileCloseLoading: boolean = false;
	private toggleEventOutcomeID: number = -1;
	private hasParent: boolean = false;
	private caseTypesForUserObj: object = {};
	private isLoading: boolean = false;
	private prevGroupId: number = 0;
	private async mounted() {
		this.isLoading = true;
		if (this.raiseToCaseManagementEnabled) {
			await this.fetchCaseTypesForUser();

			// Build object for O(1) look up when populating
			// title via ID in generic table row.
			this.caseTypesForUser.forEach((caseType) => {
				this.caseTypesForUserObj[caseType.id] = caseType.title;
			});
		}
		await this.fetchEventCategoriesList();
		if (this.isCollapseParentCategoryByDefaultEnabled) {
			this.eventCategoriesList.filter(x => !x.parentID).forEach(event => this.hideChildren(event));
		}
		this.isLoading = false;
	}

	private get prevSelectedGroupId(){
		return this.prevGroupId;
	}

	private get groupIdsToChildrenAsArray(): number[] {
		return this.groupIdsToChildren ? Array.from(this.groupIdsToChildren) : [];
	}

	/**
	 * collapseParentCategoryByDefault pref check
	 */
	private get isCollapseParentCategoryByDefaultEnabled() {
		return this.user.collapseParentCategoryByDefault ?? false;
	}

	/**
	 * Feature flag and permission check.
	 */
	public get IsEventCategorySetupEnabled() {
		return get(this.featuresList, ["EventCategorySetup"]) && this.getPermissions.canEditEventCategorySetup;
	}

	/**
	 * Feature flag check for whether the ability to raise to case management is enabled.
	 */
	public get raiseToCaseManagementEnabled() {
		return get(this.featuresList, ["CaseManagement", "RaiseToCaseManagement"]);
	}

	/**
	 * Getter which returns true when user has a selected parent outcome in the table modal.
	 */
	public get isParentSelected() {
		if (this.getModalRow != null && this.getModalRow?.parentID && this.getModalRow?.groupID == null ) {
			const outcome = this.eventCategoriesList.find((a) => {
				return a.eventOutcomeID == this.getModalRow.parentID;
			});
			this.getGroupIdsToChildren(outcome.groupID);
			return true;
		} else if(this.getModalRow != null && this.getModalRow?.groupID && this.getModalRow?.parentID == null){
			this.getGroupIdsToChildren(null);
			return true;
		}
		else{
			return true;
		}
	}

	/**
	 * Method to return title of case type via template ID
	 */
	public getTitleForTemplateId(templateId: string) {
		if (templateId && this.caseTypesForUserObj[templateId]) {
			return this.caseTypesForUserObj[templateId];
		}
		return "";
	}

	/**
	 * Method to add new event outcome.
	 * @param outcome the event outcome object to be added.
	 */
	public async addOutcome(outcome: EventCategory) {
		if (outcome) {
			const eventOutcome: EventCategory = {
				eventOutcomeID: outcome.eventOutcomeID,
				parentID: outcome.parentID,
				title: outcome.title,
				groupID: outcome.groupID ? outcome.groupID : -1,
				isMobileClose: outcome.isMobileClose ? outcome.isMobileClose : false,
				autoPreserve: outcome.autoPreserve ? outcome.autoPreserve : false,
				raiseDispatch: outcome.raiseDispatch ? outcome.raiseDispatch : false,
				raiseIncidentReport: outcome.raiseIncidentReport ? outcome.raiseIncidentReport : false,
				isActivityLog: outcome.isActivityLog ? outcome.isActivityLog : false,
				isAlarm: outcome.isAlarm ? outcome.isAlarm : false,
				isPatrol: outcome.isPatrol ? outcome.isPatrol : false,
				isIncident: outcome.isIncident ? outcome.isIncident : false,
				isParkedNote: outcome.isParkedNote ? outcome.isParkedNote : false,
				isOpen: outcome.isOpen,
				hidden: outcome.hidden,
				raiseToCasesTemplate: outcome.raiseToCasesTemplate,
			};
			await this.addEventCategory(eventOutcome);
		}
	}

	/**
	 * Method to update event outcome.
	 * @param outcome The event outcome to be updated.
	 */
	public async updateOutcome(outcome: EventCategory) {
		if (outcome) {
			try {
				await this.updateEventCategory(outcome);
			} catch(ex) {
				this.$notify({
					type: "error",
					title: "Error",
					text: ex.response.data,
				});
			}
		}
	}

	/**
	 * Method to delete event outcome and all its children.
	 * @param outcome The event outcome to be deleted.
	 */
	public async deleteOutcome(outcome: EventCategory) {
		if (outcome) {
			await this.deleteEventCategory(outcome);
		}
	}

	/**
	 * Method to determine the level of each event outcome.
	 */
	private getOutcomeLevel(level: number, outcome: EventCategory) {
		if (!outcome || !outcome.parentID) {
			return level;
		} else {
			var parent = this.eventCategoriesList.find((a) => {
				return a.eventOutcomeID == outcome.parentID;
			});
			let newLevel = level + 1;
			return this.getOutcomeLevel(newLevel, parent);
		}
	}

	/**
	 * Method to show all children of selected outcome.
	 */
	private showChildren(outcome: EventCategory) {
		this.toggleTreeIconState(outcome.eventOutcomeID, "open");
		this.getChildren(outcome.eventOutcomeID).forEach((child) => {
			if (outcome.hasChildren == true && outcome.isOpen == false) {
				return;
			}
			this.showOutcome(child);
		});
	}

	/**
	 * Method to hide all children for selected outcome.
	 */
	private hideChildren(outcome: EventCategory) {
		this.toggleTreeIconState(outcome.eventOutcomeID, "closed");
		this.getChildren(outcome.eventOutcomeID).forEach((child) => {
			if (child.hasChildren) {
				this.hideChildren(child);
			}
			this.hideOutcome(child);
		});
	}

	/**
	 * Method to toggle the state of an eventoutcome.
	 */
	private toggleTreeIconState(outcomeId: number, state: string) {
		var outcome = this.eventCategoriesList.find((a) => {
			return a.eventOutcomeID == outcomeId;
		});
		state == "open" ? this.showOutcomeSubTree(outcome) : this.hideOutcomeSubTree(outcome);
	}

	/**
	 * Method to get next level children for outcome.
	 */
	private getChildren(outcomeId: number) {
		let c = this.eventCategoriesList.filter((outcome) => {
			return outcome.parentID == outcomeId;
		});
		return c;
	}
	
	private onModalOpen(row): void {
		this.setIsLoading(true);
	}
	
	/**
	 * Method to watch the parentid and if it has changed, update the area tree state 
	 * with only the children of the new group id for that category
	 */
	@Watch("getModalRow.parentID")
	public async updateGroupsList(newValue: number,oldValue: number){
		if(this.getModalRow != null && newValue != oldValue && (this.getModalRow.eventOutcomeID == this.prevSelectedGroupId)){
			const outcome = this.eventCategoriesList.find((a) => {
				return a.eventOutcomeID == newValue;
			});
			if(outcome != null && outcome.groupID !== null)
				await this.getGroupIdsToChildren(outcome.groupID);
			else
				await this.getGroupIdsToChildren(null);
			if(outcome != null && newValue != this.getModalRow.eventOutcomeID){
				Vue.set(this.genericTableRef.$refs.GenericUpdateModal.newEntry,"groupID",null);
				this.$forceUpdate();
			}
		}
		else if (newValue != null)
		{
			await this.getGroupIdsToChildren(this.getModalRow.groupID);
		}
		this.prevGroupId = this.getModalRow.eventOutcomeID;
	}
	@Watch("getModalRow.eventOutcomeID")
	public async initialGroupsList(newValue: number,oldValue: number){
		const outcome = this.eventCategoriesList.find((a) => {
			return a.eventOutcomeID == newValue;
		});
		if(outcome == null)
			await this.getGroupIdsToChildren(null);
	}
	
	private get TableHeadings() {
		return [
			{
				title: "Title",
				key: "title",
				order: 0,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Title of event outcome",
				searchable: true,
				visible: true,
				dataType: "text",
				useCustomCell: true,
				width: "20%",
			} as TableHeader,
			{
				title: "Area Name",
				key: "areaName",
				order: 1,
				sortOrder: 1,
				sortOrderReversed: false,
				description: "Area Name",
				visible: true,
				searchable: true,
				dataType: "text",
			} as TableHeader,
			{
				title: "Raise to Case Management",
				key: "raiseToCasesTemplate",
				order: 2,
				sortOrder: 2,
				sortOrderReversed: false,
				description: "Case Type",
				visible: this.raiseToCaseManagementEnabled,
				searchable: true,
				dataType: "text",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Event",
				key: "isAlarm",
				order: 3,
				sortOrder: 3,
				sortOrderReversed: false,
				description: "Event",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Tour",
				key: "isPatrol",
				order: 4,
				sortOrder: 4,
				sortOrderReversed: false,
				description: "Tour",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Incident",
				key: "isIncident",
				order: 5,
				sortOrder: 5,
				sortOrderReversed: false,
				description: "Incident",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Preserve",
				key: "autoPreserve",
				order: 6,
				sortOrder: 6,
				sortOrderReversed: false,
				description: "Preserve",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Raise Dispatch",
				key: "raiseDispatch",
				order: 7,
				sortOrder: 7,
				sortOrderReversed: false,
				description: "Raise Dispatch",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Raise Incident Report",
				key: "raiseIncidentReport",
				order: 8,
				sortOrder: 8,
				sortOrderReversed: false,
				description: "Raise Incident Report",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Activity Category",
				key: "isActivityLog",
				order: 9,
				sortOrder: 9,
				sortOrderReversed: false,
				description: "Activity Category",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Mobile Quick Close",
				key: "isMobileClose",
				order: 10,
				sortOrder: 10,
				sortOrderReversed: false,
				description: "Mobile Quick Close",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
			{
				title: "Parked Note",
				key: "isParkedNote",
				order: 11,
				sortOrder: 11,
				sortOrderReversed: false,
				description: "Parked Note",
				searchable: false,
				visible: true,
				dataType: "checkbox",
				useCustomCell: true,
			} as TableHeader,
		];
	}

	private GetModalItems() {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				placeholder: "Event Title",
				maxLength: 50,
				visible: true,
			},
			{
				title: "Parent Event Outcome",
				key: "parentID",
				dataType: "component",
				readOnly: false,
				data: EventOutcomeSelect,
				required: false,
				visible: true,
			},
			{
				title: "Area",
				key: "groupID",
				dataType: "component",
				readOnly: false,
				data: AreaTreeSelect,
				props: {
						reduce: node => node.id,
						clearable: true,
						appendToBody: true,
						filter: (areaNode: AreaNode) =>  this.groupIdsToChildrenAsArray.length > 0 ? this.groupIdsToChildrenAsArray.includes(areaNode.id) : true
					},
				required: this.isParentSelected,
				visible: true,
			},
			{
				title: "Case Type",
				key: "raiseToCasesTemplate",
				dataType: "component",
				readOnly: false,
				data: CaseTypeSelect,
				props: {
					caseTypes: this.caseTypesForUser,
				},
				required: false,
				visible: this.raiseToCaseManagementEnabled == true,
			},
			{
				title: "Event",
				key: "isAlarm",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Tour",
				key: "isPatrol",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Incident",
				key: "isIncident",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Auto Preserve",
				key: "autoPreserve",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Raise Dispatch",
				key: "raiseDispatch",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Raise Incident Report",
				key: "raiseIncidentReport",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Activity Category",
				key: "isActivityLog",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Mobile Quick Close",
				key: "isMobileClose",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
			{
				title: "Parked Note",
				key: "isParkedNote",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
			},
		] as ModalItem[];
	}
}
